import { useEffect, useRef, useMemo } from "react";
import { EditorState, Compartment, Prec, type Extension } from "@codemirror/state";
import { EditorView, keymap, placeholder as placeholderExt, lineNumbers, highlightActiveLineGutter } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { lintKeymap } from "@codemirror/lint";
import { bracketMatching, foldGutter, foldKeymap, indentOnInput, syntaxHighlighting } from "@codemirror/language";

// Theme for CodeMirror — uses CSS vars so it adapts to light/dark
const spuerhundTheme = EditorView.theme({
  "&": {
    backgroundColor: "transparent",
    color: "var(--color-zinc-200)",
    fontSize: "13px",
    height: "100%",
  },
  ".cm-content": {
    caretColor: "var(--color-editor-caret)",
    fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace",
    padding: "12px 0",
    lineHeight: "1.5",
  },
  ".cm-cursor": {
    borderLeftColor: "var(--color-editor-caret)",
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: "var(--color-editor-caret)",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-gutters": {
    backgroundColor: "transparent",
    color: "var(--color-zinc-700)",
    border: "none",
    paddingLeft: "8px",
    paddingRight: "4px",
  },
  ".cm-gutterElement": {
    padding: "0 8px 0 0",
    minWidth: "2ch",
  },
  ".cm-activeLineGutter": {
    backgroundColor: "transparent",
    color: "var(--color-zinc-500)",
  },
  ".cm-activeLine": {
    backgroundColor: "var(--color-selection-line)",
  },
  ".cm-selectionBackground": {
    backgroundColor: "var(--color-selection) !important",
  },
  "&.cm-focused .cm-selectionBackground": {
    backgroundColor: "var(--color-selection-active) !important",
  },
  ".cm-selectionMatch": {
    backgroundColor: "var(--color-selection-match) !important",
  },
  ".cm-matchingBracket": {
    backgroundColor: "var(--color-bracket-match)",
    outline: "1px solid var(--color-bracket-outline)",
  },
  ".cm-tooltip": {
    backgroundColor: "var(--color-surface-raised)",
    color: "var(--color-zinc-200)",
    border: "1px solid var(--color-border)",
    borderRadius: "6px",
  },
  ".cm-tooltip-autocomplete": {
    backgroundColor: "var(--color-surface-raised)",
    color: "var(--color-zinc-200)",
    "& > ul": {
      backgroundColor: "var(--color-surface-raised)",
      color: "var(--color-zinc-200)",
    },
    "& > ul > li": {
      padding: "2px 8px",
      color: "var(--color-zinc-200)",
    },
    "& > ul > li[aria-selected]": {
      backgroundColor: "var(--color-surface-overlay)",
      color: "var(--color-zinc-200)",
    },
  },
  ".cm-completionLabel": {
    color: "var(--color-zinc-200)",
  },
  ".cm-completionMatchedText": {
    color: "var(--color-accent-light)",
  },
  ".cm-completionDetail": {
    color: "var(--color-zinc-500)",
  },
  ".cm-completionIcon": {
    color: "var(--color-zinc-500)",
  },
  ".cm-tooltip.cm-completionInfo": {
    backgroundColor: "var(--color-surface-raised)",
    color: "var(--color-zinc-200)",
    border: "1px solid var(--color-border)",
  },
  ".cm-panels": {
    backgroundColor: "var(--color-surface-raised)",
    color: "var(--color-zinc-200)",
  },
  ".cm-panel.cm-search": {
    backgroundColor: "var(--color-surface-raised)",
    border: "1px solid var(--color-border)",
  },
  ".cm-foldPlaceholder": {
    backgroundColor: "var(--color-surface-overlay)",
    border: "none",
    color: "var(--color-editor-fold)",
  },
  ".cm-line": {
    padding: "0 12px",
    lineHeight: "1.6",
  },
  ".cm-placeholder": {
    color: "var(--color-zinc-600)",
    fontStyle: "italic",
  },
}, { dark: true });

// Syntax highlighting colors
import { HighlightStyle } from "@codemirror/language";
import { tags } from "@lezer/highlight";

const spuerhundHighlight = syntaxHighlighting(HighlightStyle.define([
  { tag: tags.keyword, color: "var(--color-editor-keyword)" },
  { tag: tags.typeName, color: "var(--color-editor-type)" },
  { tag: tags.propertyName, color: "var(--color-editor-property)" },
  { tag: tags.string, color: "var(--color-editor-string)" },
  { tag: tags.number, color: "var(--color-editor-number)" },
  { tag: tags.bool, color: "var(--color-editor-number)" },
  { tag: tags.null, color: "var(--color-editor-operator)" },
  { tag: tags.comment, color: "var(--color-editor-comment)", fontStyle: "italic" },
  { tag: tags.variableName, color: "var(--color-editor-variable)" },
  { tag: tags.operator, color: "var(--color-editor-operator)" },
  { tag: tags.punctuation, color: "var(--color-editor-comment)" },
  { tag: tags.definition(tags.variableName), color: "var(--color-editor-type)" },
  { tag: tags.atom, color: "var(--color-editor-keyword)" },
]));

interface CodeMirrorEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  extensions?: Extension[];
  className?: string;
  readOnly?: boolean;
  onExecute?: () => void;
}

export function CodeMirrorEditor({
  value,
  onChange,
  placeholder,
  extensions: extraExtensions = [],
  className = "",
  readOnly = false,
  onExecute,
}: CodeMirrorEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onExecuteRef = useRef(onExecute);
  onExecuteRef.current = onExecute;
  const isApplyingExternalValueRef = useRef(false);

  // Compartment for dynamically swappable language extensions
  const langCompartment = useMemo(() => new Compartment(), []);
  const readOnlyCompartment = useMemo(() => new Compartment(), []);

  // Create editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        if (isApplyingExternalValueRef.current) {
          return;
        }
        onChangeRef.current?.(update.state.doc.toString());
      }
    });

    const readOnlyExts = readOnly
      ? [
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          EditorView.theme({ ".cm-cursor": { display: "none !important" } }),
        ]
      : [];

    const state = EditorState.create({
      doc: value,
      extensions: [
        Prec.highest(keymap.of([
          {
            key: "Mod-Enter",
            run: () => {
              onExecuteRef.current?.();
              return true;
            },
          },
        ])),
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          ...completionKeymap,
          ...foldKeymap,
          ...lintKeymap,
          indentWithTab,
        ]),
        history(),
        closeBrackets(),
        bracketMatching(),
        indentOnInput(),
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSelectionMatches(),
        foldGutter(),
        autocompletion(),
        spuerhundTheme,
        spuerhundHighlight,
        ...(placeholder ? [placeholderExt(placeholder)] : []),
        langCompartment.of(extraExtensions),
        readOnlyCompartment.of(readOnlyExts),
        updateListener,
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []); // only mount once

  // Reconfigure language extensions when they change (e.g. graphql → json)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: langCompartment.reconfigure(extraExtensions),
    });
  }, [extraExtensions, langCompartment]);

  // Reconfigure readOnly state
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const exts = readOnly
      ? [
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          EditorView.theme({ ".cm-cursor": { display: "none !important" } }),
        ]
      : [];
    view.dispatch({
      effects: readOnlyCompartment.reconfigure(exts),
    });
  }, [readOnly, readOnlyCompartment]);

  // Sync value from outside (e.g. switching queries)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentValue = view.state.doc.toString();
    if (currentValue !== value) {
      isApplyingExternalValueRef.current = true;
      view.dispatch({
        changes: { from: 0, to: currentValue.length, insert: value },
      });
      isApplyingExternalValueRef.current = false;
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={`flex-1 overflow-auto ${className}`}
    />
  );
}
